import React, { FC, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { OptionDataPayload } from '../../shared/options';
import { ProductEligibility, ProductModel } from '../../shared/orders';
import { useTranslation } from '../translations';
import { Fetcher, useFetcher, useUserSafe } from '../utils';
import { captureError } from '../utils/errorHandling';
import { ActionButton } from './ActionButton';
import { MaterialIconText } from './MaterialIconText';

import './AddToCartButton.scss';

interface Props {
  readonly product: ProductModel;
  readonly eligibility: ProductEligibility;
  readonly quantity: number;
  readonly productOptions: OptionDataPayload;
  readonly onAddedToCart: (success: boolean) => void;
  readonly onFailure: (err: Error) => void;
}

interface CartStatus {
  canAutoFulfill?: boolean;
  orderId: number;
}

export const AddToCartButton: FC<Props> = ({
  product,
  quantity,
  eligibility,
  productOptions,
  onAddedToCart,
  onFailure,
}) => {
  const { ts } = useTranslation();
  const user = useUserSafe();
  const history = useHistory();

  const cartStatus = useFetcher<CartStatus>(async () => {
    if (product.price > 0) {
      // If the product is free, we can'ts auto-fulfill it.
      return { eligibility: 'eligible', canAutoFulfill: false, orderId: 0 };
    }

    // Check for an active order
    const order = await api.getActiveOrder(user.id, true);

    if (order.breakdown.total > 0) {
      // If the order total is greater than zero, we can'ts auto-fulfill it.
      return { eligibility: 'eligible', canAutoFulfill: false, orderId: order.id };
    }

    // Otherwise, we can auto-fulfill it!
    return { eligibility: 'eligible', canAutoFulfill: true, orderId: order.id };
  }, [product, user]);

  const onSuccess = useCallback(async () => {
    if (cartStatus.data?.canAutoFulfill && cartStatus.data.orderId > 0) {
      // Auto-fulfill the order
      try {
        await api.settleOrder(cartStatus.data.orderId);
        onAddedToCart(true);
      } catch (error) {
        captureError(error as Error);
        history.push('/cart');
      }
    } else {
      onAddedToCart(true);
    }
  }, [onAddedToCart, cartStatus, history]);

  if (cartStatus.loading) {
    return (
      <Button block color="secondary" disabled outline>
        {ts('add_to_cart_button.checking_order_status')}
      </Button>
    );
  }

  if (cartStatus.error) {
    return <Fetcher result={cartStatus} />;
  }

  if (!eligibility.canPurchase && eligibility.alreadyHasTicket) {
    return (
      <p className="add-to-cart-blocked" id="addToCartBlocked">
        <MaterialIconText name="block" small type="danger">
          {ts('add_to_cart_button.already_purchased_one')}
        </MaterialIconText>
      </p>
    );
  }

  return (
    <ActionButton
      action={async () => {
        await api.addProductToCart(product.id, { quantity, options: productOptions });
      }}
      block
      color="success"
      disabled={!eligibility.canPurchase}
      id="addToCart"
      onFailure={onFailure}
      onSuccess={onSuccess}
    >
      {ts(
        cartStatus.data.canAutoFulfill ? 'add_to_cart_button.complete_order' : 'common.add_to_cart',
      )}
    </ActionButton>
  );
};
